import { SideNavbar as SideNavbarUI } from "@blend-ed/blendx-ui";
import { getConfig } from '@edx/frontend-platform';
import { getCookies } from '@edx/frontend-platform/i18n/lib';
import { AppContext } from "@edx/frontend-platform/react";
import PropTypes from "prop-types";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useDarkMode } from "../../hooks/useDarkMode";
import { useOrganizations, useOrgLogos } from "../../hooks/useOrganizations";
import { getFeatureToggle } from '../../utils/getFeatureToggle';
import { getRoleAndPermissions } from "../../utils/getRoleAndPermissions";
import getDashboardMenu from './DashboardMenu';
import getStaffDashboardMenu from './StaffDashboardMenu';

const SideNavbar = ({ children, staffDashboard, collapsed }) => {
  const { authenticatedUser } = useContext(AppContext);
  const { ACCOUNT_PROFILE_URL, STAFF_DASHBOARD_URL, DASHBOARD_URL, LOGOUT_URL, LMS_BASE_URL } = getConfig();
  const { DashboardMenu, DashboardFooter, ProfileMenu } = getDashboardMenu();
  const { StaffDashboardMenu, StaffDashboardFooter } = getStaffDashboardMenu();
  const { role } = getRoleAndPermissions();
  const { data: orgList } = useOrganizations();
  const { logo: logoLight, logoTrademark: logoTrademarkLight, logoDark, logoDarkTrademark } = useOrgLogos();
  const { features_hide_staff_as_student, features_enable_organizations } = getFeatureToggle();
  const { isDarkMode } = useDarkMode();
  const [currentTheme, setCurrentTheme] = useState(isDarkMode);
  const domain = `.${LMS_BASE_URL.split('://')[1].split(':')[0]}`;

  // Watch for theme changes
  useEffect(() => {
    const checkTheme = () => {
      const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
      setCurrentTheme(isDark);
    };

    // Initial check
    checkTheme();

    // Watch for theme changes
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });

    return () => observer.disconnect();
  }, []);

  //? user data 
  const user = useMemo(() => ({
    name: authenticatedUser.name,
    username: authenticatedUser.username,
    email: authenticatedUser.email,
    role: role,
    image: authenticatedUser.profileImage?.imageUrlSmall,
    id: authenticatedUser.id
  }), [authenticatedUser]);

  //? show organization button if feature is enabled
  const organizationsLink = useMemo(() => {
    if (features_enable_organizations) {
      return staffDashboard ? STAFF_DASHBOARD_URL + "organizations" : (orgList?.length > 1 && DASHBOARD_URL + "organizations")
    }
    return null;
  }, [features_enable_organizations, staffDashboard, orgList, STAFF_DASHBOARD_URL, DASHBOARD_URL]);

  return <SideNavbarUI
    logo={logoLight}
    logoTrademark={logoTrademarkLight}
    logoDark={logoDark}
    logoDarkTrademark={logoDarkTrademark}
    logoLink={staffDashboard ? STAFF_DASHBOARD_URL : DASHBOARD_URL}
    collapsed={collapsed}
    user={user}
    dashboardMenu={staffDashboard ? StaffDashboardMenu : DashboardMenu}
    dashboardFooter={staffDashboard ? StaffDashboardFooter : DashboardFooter}
    profileMenu={staffDashboard ? [] : ProfileMenu}
    profileLink={ACCOUNT_PROFILE_URL + '/user/' + user.username}
    staffDashboardLink={!staffDashboard && (role === 'staff' || role === 'super_admin') ? STAFF_DASHBOARD_URL : null}
    organizationsLink={organizationsLink}
    dashboardLink={!features_hide_staff_as_student && staffDashboard && DASHBOARD_URL}
    isDarkMode={currentTheme}
    logoutLink={LOGOUT_URL}
    logoutOnClick={() => {
      const cookies = getCookies();
      cookies.remove('orgName', { domain, path: '/' });
      window.location.href = LOGOUT_URL;
    }}
  >
    {children}
  </SideNavbarUI>;
};

SideNavbar.propTypes = {
  children: PropTypes.node.isRequired,
  staffDashboard: PropTypes.bool,
  collapsed: PropTypes.bool,
};

SideNavbar.defaultProps = {
  staffDashboard: false,
  collapsed: false,
};

export default SideNavbar;
